import React from 'react';
import _get from 'lodash/get';
import { FormattedMessage } from '@kyruus/intl';
import type { PMCConfig } from '@kyruus/types';

import { Box } from '@biome-design-system/ui/Box';
import { visuallyHidden } from '@biome-design-system/styles';
import { Interpretation } from './Interpretation';
import { SearchTermText } from './SearchTermText';
import { getSearchSummaryMessage } from './utils';
import { isSearchRefinerSkipped } from 'Src/utils/search-common';
import { SearchSummary } from 'Src/types';
import { type Log, type GetUpdatedSearch } from 'Src/types';
import { messages } from '../messages';

interface SearchSummaryMessageProps {
  log: Log;
  getUpdatedSearch: GetUpdatedSearch;
  searchSummary: SearchSummary;
  totalProviders: number;
  isMapSearch: boolean;
  hideSearchDescription?: boolean;
  queryString: string;
  config: PMCConfig;
}

export const SearchSummaryMessage = ({
  searchSummary,
  totalProviders,
  isMapSearch,
  hideSearchDescription,
  queryString,
  log,
  config,
  getUpdatedSearch
}: SearchSummaryMessageProps) => {
  const locationFilterName =
    searchSummary.display_location || searchSummary.location;
  const showInterpretation =
    searchSummary.interpretation?.length > 0 &&
    _get(config, 'search_results_page_v9.show_semantic_interpretation', true);
  if (showInterpretation) {
    return (
      <Interpretation
        totalProviders={totalProviders}
        searchTerms={searchSummary.interpretation}
        locationFilterName={locationFilterName}
        log={log}
        getUpdatedSearch={getUpdatedSearch}
      />
    );
  } else {
    const searchDescription = searchSummary.search_description;
    const showSearchDescription =
      !hideSearchDescription && Boolean(searchDescription);
    const searchRefinerSkipped = isSearchRefinerSkipped(queryString);
    const { messageDescriptor, testId } = getSearchSummaryMessage(
      queryString,
      isMapSearch,
      showSearchDescription
    );
    const searchTermNode = searchDescription ? (
      <SearchTermText displayText={searchDescription} />
    ) : null;

    return (
      <>
        <FormattedMessage
          {...messageDescriptor}
          values={{
            location: locationFilterName,
            providerCount: totalProviders,
            searchTerm: searchTermNode
          }}
          data-testid={testId}
        />
        {searchRefinerSkipped && (
          <FormattedMessage
            {...messages.searchIsTooGeneralAriaLive}
            values={{
              term: searchDescription,
              letUsHelp: ''
            }}
          >
            {(message) => <Box sx={visuallyHidden}>{message}</Box>}
          </FormattedMessage>
        )}
      </>
    );
  }
};
